<template>
  <div class="quiz-progress-alert" :class="`quiz-progress-alert--${status}`" role="status">
    <span class="quiz-progress-alert__icon">
      <img :src="statusIcon" alt="" class="quiz-progress-alert__icon-glyph" />
    </span>

    <p class="quiz-progress-alert__message">{{ message }}</p>

    <div class="quiz-progress-alert__progress">
      <div
        class="quiz-progress-alert__track"
        role="progressbar"
        aria-label="Quiz completion"
        :aria-valuemin="0"
        :aria-valuemax="totalCount"
        :aria-valuenow="progressCount"
      >
        <span class="quiz-progress-alert__value" :style="{ width: `${progressPercentage}%` }" />
      </div>
      <p class="quiz-progress-alert__count">{{ countLabel }}</p>
    </div>

    <StandardButton
      v-if="status === 'ready'"
      type="magenta"
      variant="primary"
      size="small"
      label="Submit"
      :disabled="disabled"
      @click="emit('submit')"
    />
  </div>
</template>

<script setup>
import { computed } from "vue";
import StandardButton from "@components/StandardButton/StandardButton.vue";

const props = defineProps({
  status: {
    type: String,
    default: "answering",
    validator: (value) => ["answering", "ready", "failed", "passed"].includes(value),
  },
  answeredCount: { type: Number, default: 0 },
  correctCount: { type: Number, default: 0 },
  totalCount: { type: Number, default: 0 },
  passedMessage: { type: String, default: "Passed" },
  disabled: { type: Boolean, default: false },
});

const emit = defineEmits(["submit"]);

const STATUS_ICONS = {
  answering: require("@components/assets/alert-circle-warning.svg"),
  ready: require("@components/assets/info-circle-brand.svg"),
  failed: require("@components/assets/alert-circle-warning.svg"),
  passed: require("@components/assets/check-circle-success.svg"),
};

const isEvaluated = computed(() => props.status === "failed" || props.status === "passed");

const statusIcon = computed(() => STATUS_ICONS[props.status] || STATUS_ICONS.answering);

const message = computed(() => {
  if (props.status === "ready") return "Submit your answers";
  if (props.status === "passed") return props.passedMessage;
  return `You must answer all ${props.totalCount} questions correctly`;
});

const progressCount = computed(() =>
  isEvaluated.value ? props.correctCount : props.answeredCount
);

const progressPercentage = computed(() => {
  if (!props.totalCount) return 0;
  return Math.round((progressCount.value / props.totalCount) * 100);
});

const countLabel = computed(() =>
  isEvaluated.value
    ? `${props.correctCount} correctly answered out of ${props.totalCount}`
    : `${props.answeredCount} answered out of ${props.totalCount}`
);
</script>

<style scoped>
.quiz-progress-alert {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px;
  border: 1px solid #d0d5dd;
  border-radius: 12px;
  background: #ffffff;
  box-shadow: 0 1px 1px rgba(16, 24, 40, 0.05);
  box-sizing: border-box;
  color: #344054;
  font-family: Inter, sans-serif;
}

.quiz-progress-alert__icon {
  position: relative;
  flex: 0 0 auto;
  width: 20px;
  height: 20px;
  margin-left: 8px;
  color: #dc6803;
}

.quiz-progress-alert--ready .quiz-progress-alert__icon {
  color: #7f56d9;
}

.quiz-progress-alert--passed .quiz-progress-alert__icon {
  color: #079455;
}

/* Concentric halo rings around the status icon */
.quiz-progress-alert__icon::before,
.quiz-progress-alert__icon::after {
  position: absolute;
  border: 1.667px solid currentColor;
  border-radius: 50%;
  content: "";
}

.quiz-progress-alert__icon::before {
  inset: -3.33px;
  opacity: 0.3;
}

.quiz-progress-alert__icon::after {
  inset: -7.5px;
  opacity: 0.1;
}

.quiz-progress-alert__icon-glyph {
  position: absolute;
  top: 8.33%;
  left: 8.33%;
  width: 83.34%;
  height: 83.34%;
}

.quiz-progress-alert__message {
  flex: 1 1 auto;
  min-width: 0;
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
}

.quiz-progress-alert__progress {
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  gap: 12px;
  width: 320px;
}

.quiz-progress-alert__track {
  flex: 1 1 auto;
  min-width: 0;
  height: 8px;
  overflow: hidden;
  border-radius: 9999px;
  background: #eaecf0;
}

.quiz-progress-alert__value {
  display: block;
  height: 100%;
  border-radius: inherit;
  background: #7f56d9;
  transition: width 0.2s ease;
}

.quiz-progress-alert--passed .quiz-progress-alert__value {
  background: #17b26a;
}

.quiz-progress-alert__count {
  flex: 0 0 auto;
  margin: 0;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  white-space: nowrap;
}

@media (max-width: 760px) {
  .quiz-progress-alert {
    align-items: flex-start;
    flex-direction: column;
  }

  .quiz-progress-alert__progress {
    width: 100%;
  }
}
</style>
